import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { Member, Role, UpdateMember } from "@/model";
import { useUpdateMember } from "@/query/member";
import { useScenarioId, useModal } from "@/hook";
import Button from "@/component/Button";
import { Modal } from "@/component/modal";
import { errorToast, successToast } from "@/component/Toast";

import RoleDropDown from "./RoleDropDown";


export const MEMBER_MODIFY_MODAL_ID = "member-modify-modal-id";

const MemberModifyModal: React.FC = () => {
    const intl = useIntl();
    const scenarioId = useScenarioId();
    const modal = useModal();
    const member = modal.getData(MEMBER_MODIFY_MODAL_ID) as Member | undefined;

    const [role, setRole] = useState<Role>();
    const { mutateAsync: updateMember, isPending: isUpdating } = useUpdateMember(scenarioId);

    const onClickConfirm = async () => {
        if (!role || !member){
            return;
        }

        const form: UpdateMember = {
            role_id: role._id ?? role.id,
            member_id: member._id,
            default: role.default ?? true
        };

        await updateMember(form)
            .then(() => {
                successToast(intl.formatMessage({ id: "t003" }));
                modal.close(MEMBER_MODIFY_MODAL_ID);
            })
            .catch(() => {
                errorToast(intl.formatMessage({ id: "t004" }));
            });
    };

    useEffect(() => {
        if (!member?.roles || member.roles.length === 0) {
            return;
        }
        setRole(member.roles[0]);
    }, [member]);

    if (!member) {
        return null;
    }
    return (
        <Modal id={MEMBER_MODIFY_MODAL_ID}
               size="md"
               title={intl.formatMessage({ id: "i11010" })}
               onClose={() => setRole(undefined)}
        >
            <div className="modal-body-8">
                <fieldset className="flex flex-col gap-6">
                    <legend className="sr-only">
                        {intl.formatMessage({ id: "i11010" })}
                    </legend>
                    <div className="flex flex-col gap-1.5">
                        <div id="modify-username-label"
                             className="field-label"
                        >
                            {intl.formatMessage({ id: "i11011" })}
                        </div>
                        <div aria-labelledby="modify-username-label"
                             className="modal-description py-2"
                        >
                            {member?.user.username}
                        </div>
                    </div>

                    <div className="flex flex-col gap-1.5">
                        <div id="modify-nickname-label"
                             className="field-label"
                        >
                            {intl.formatMessage({ id: "b0021" })}
                        </div>
                        <div aria-labelledby="modify-nickname-label"
                             className="modal-description py-2"
                        >
                            {member?.user.nickname}
                        </div>
                    </div>

                    <div className="flex flex-col gap-1.5">
                        <div className="field-label">
                            {intl.formatMessage({ id: "i11015" })}
                        </div>
                        <RoleDropDown role={role}
                                      aria-label={intl.formatMessage({ id: "i11015" })}
                                      onChange={setRole}
                        />
                    </div>
                </fieldset>

                <div className="modal-btn-group">
                    <Button size="sm"
                            variant="secondary"
                            disabled={isUpdating}
                            onClick={() => modal.close(MEMBER_MODIFY_MODAL_ID)}
                    >
                        {intl.formatMessage({ id: "b0002" })}
                    </Button>
                    <Button size="sm"
                            variant="primary"
                            disabled={isUpdating}
                            onClick={onClickConfirm}
                    >
                        {intl.formatMessage({ id: "b0001" })}
                    </Button>
                </div>
            </div>

        </Modal>
    );
};

export default MemberModifyModal;